/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../../../modules/auth'
import {RiskListToolbar} from '../components/header/RiskListToolbar'
import db from '../../../../services/firebase'
import {RiskListLoading} from '../components/loading/RiskListLoading'
import {Helmet} from 'react-helmet'
import {collection, deleteDoc, doc, getDoc, onSnapshot, updateDoc} from 'firebase/firestore'
import {EditRiskModal} from '../components/EditRiskModal'
import {useParams} from 'react-router-dom'
import {useLocation} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {Overlay, OverlayTrigger, Popover, Tooltip} from 'react-bootstrap'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import Searchbar from 'app/components/Searchbar'
import OwnerDisplay from 'app/components/OwnerDisplay'
import Button from 'app/components/Button'
import AssignOwnerForm from 'app/components/AssignOwnerForm'
import useAuditContext from 'app/Hooks/useAuditContext'

type LocationState = {
  showToast?: boolean
  toastMessage?: string
}

const RiskManagement: React.FC = () => {
  const [risk, setRisk] = useState([] as any[])
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [riskID, setRiskID] = useState('')
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const {id: riskId} = useParams<{id: string}>()
  const location = useLocation()
  const [highlightedRiskId, setHighlightedRiskId] = useState<string | null>(null)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [show1, setShow1] = useState(false)
  const target = useRef(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [personnels, setPersonnels] = useState<any[]>([])
  const params = new URLSearchParams(location.search)
  const id = params.get('id')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedRisk, setSelectedRisk] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 3000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    getRisk()
  }, [])

  useEffect(() => {
    setFilteredItems(risk)
  }, [risk])

  useEffect(() => {
    if (id) {
      setRiskID(id)
      setIsModalOpen(true)
    }
  }, [id])

  useEffect(() => {
    const state = location.state as LocationState | null
    if (state?.showToast) {
      showSuccessToast({message: state.toastMessage || 'Risk has been updated successfully!'})
    }
  }, [location])

  useEffect(() => {
    getRisk()
    if (riskId) {
      setHighlightedRiskId(riskId)
      setTimeout(() => {
        setHighlightedRiskId(null)
      }, 5000)
    }
  }, [riskId])

  const getRisk = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'risks')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        setRisk(risksData)
        setShowEmptyState(false)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          role: doc.data().role,
        }))
        .filter((user) => user.role === 'Admin' || user.role === 'Owner')
      setPersonnels(risksData)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(risk)
      setSearchLength(false)
    } else {
      const filteredItems = risk.filter((user) =>
        user.riskSource.toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [searchTerm, risk])

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const deleteHandler = async (riskData) => {
    setSelectedRisk(riskData)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'risks')
      const docRef = doc(policyRef, selectedRisk.id)
      await deleteDoc(docRef)
      if (selectedRisk.added_controls && selectedRisk.added_controls.length > 0) {
        const controlUpdates = selectedRisk.added_controls.map(async (control) => {
          const controlRef = doc(collection(tenantRef, 'controls'), control.id)

          const controlDoc = await getDoc(controlRef)
          if (controlDoc.exists()) {
            const controlData = controlDoc.data()

            const updatedRiskScenario = controlData.risk_scenario.filter(
              (risk) => risk.riskId !== selectedRisk.id
            )

            await updateDoc(controlRef, {risk_scenario: updatedRiskScenario})
          }
        })

        await Promise.all(controlUpdates)
      }
      getRisk()
      showSuccessToast({message: 'Risk scenario has been deleted successfully!'})
    } catch (error) {
      showErrorToast({
        message: 'An error occurred while deleting the risks and updating associated controls.',
      })
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    setRiskID(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  const handleSelectAllChange = (e) => {
    setSelectAll(e.target.checked)
    if (e.target.checked) {
      setCheckedItems(filteredItems.map((item) => item.id))
    } else {
      setCheckedItems([])
    }
  }

  const getBadgeClass = (risk) => {
    if (risk < 5) {
      return {backgroundColor: '#0E9F6E'}
    } else if (risk >= 5 && risk <= 14) {
      return {backgroundColor: '#ffc107'}
    } else if (risk >= 15 && risk < 25) {
      return {backgroundColor: '#dc3545'}
    } else if (risk >= 25) {
      return {backgroundColor: '#A7222F'}
    } else {
      return {backgroundColor: ''}
    }
  }

  const getRiskLevelText = (risk) => {
    if (risk < 5) {
      return 'Low'
    } else if (risk >= 5 && risk <= 14) {
      return 'Medium'
    } else if (risk >= 15 && risk < 25) {
      return 'High'
    } else if (risk >= 25) {
      return 'Critical'
    } else {
      return ''
    }
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => (checked ? [...prev, value] : prev.filter((item) => item !== value)))
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Risk Management</title>
      </Helmet>

      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
        <div className='card-toolbar'>
          {checkedItems.length > 0 ? (
            <>
              {checkedItems.length > 0 ? (
                <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                  <span>
                    <p className='pt-4'>{checkedItems.length} Selected</p>
                  </span>
                </div>
              ) : null}
              <div style={{display: 'flex', alignItems: 'center'}}>
                <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                  <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                </div>
                <div ref={target}>
                  <Button btnType='primary' onClick={() => setShow1(!show1)}>
                    Assign Owner
                  </Button>
                </div>
              </div>
              <Overlay
                show={show1}
                target={target.current}
                placement='bottom'
                container={document.body}
                containerPadding={10}
              >
                {(props) => (
                  <Popover id='popover-contained' {...props}>
                    <div ref={overlayRef}>
                      <Popover.Body onClick={(e) => e.stopPropagation()}>
                        <AssignOwnerForm
                          personnels={personnels}
                          checkedItems={checkedItems}
                          onClose={() => setShow1(false)}
                          onSuccess={() => {
                            setCheckedItems([])
                            setSelectAll(false)
                          }}
                          currentUser={currentUser}
                          collectionName='risks'
                          fieldName='risk_owner'
                        />
                      </Popover.Body>
                    </div>
                  </Popover>
                )}
              </Overlay>
            </>
          ) : (
            <RiskListToolbar currentAuditContext={currentAuditContext} />
          )}
        </div>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive' style={{minHeight: '40vh'}}>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                <th className='w-25px'>
                  <div className='form-check form-check-sm form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={selectAll}
                      data-kt-check='true'
                      data-kt-check-target='.widget-9-check'
                      onChange={handleSelectAllChange}
                    />
                  </div>
                </th>
                <th className='min-w-200px'>SCENARIO</th>
                <th className='min-w-100px'>INHERENT RISK</th>
                <th className='min-w-100px'>RESIDUAL RISK</th>
                <th className='min-w-100px'>MAPPED CONTROLS</th>
                <th className='min-w-150px'>OWNER</th>
                <th className='min-w-100px'>STATUS</th>
                <th className='min-w-10px'></th>
              </tr>
            </thead>
            <ToastContainer />
            <EditRiskModal id={riskID} isOpen={isModalOpen} onClose={handleCloseModal} />

            <tbody>
              {filteredItems && filteredItems.length > 0 ? (
                filteredItems
                  .sort((a, b) => a.riskSource.localeCompare(b.riskSource))
                  .map((riskData) => {
                    return (
                      <tr
                        key={riskData.id}
                        style={{
                          backgroundColor: riskData.id === highlightedRiskId ? '#f6f6f6' : '',
                          transition: 'background-color 0.3s ease-in-out',
                        }}
                      >
                        <td>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <input
                              className='form-check-input widget-9-check'
                              type='checkbox'
                              value={riskData.id}
                              checked={selectAll || checkedItems.includes(riskData.id)}
                              onChange={handleCheckChange}
                            />
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <a
                                href='#'
                                className='text-dark fw-bold text-hover-primary d-block fs-6'
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleOpenModal(riskData.id)
                                }}
                              >
                                {riskData?.riskSource}
                              </a>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className='badge border p-2 rounded-pill'>
                              <span
                                className={`badge border rounded-circle mx-2 `}
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  padding: '3px',
                                  ...getBadgeClass(
                                    riskData.inherent_impact * riskData.inherent_likelihood
                                  ),
                                }}
                              >
                                {' '}
                              </span>
                              {getRiskLevelText(
                                riskData.inherent_impact * riskData.inherent_likelihood
                              )}
                              : {riskData.inherent_impact * riskData.inherent_likelihood}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className='badge border p-2 rounded-pill'>
                              <span
                                className={`badge border rounded-circle mx-2`}
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  padding: '3px',
                                  ...getBadgeClass(
                                    riskData.residual_impact * riskData.residual_likelihood
                                  ),
                                }}
                              >
                                {' '}
                              </span>
                              {getRiskLevelText(
                                riskData.residual_impact * riskData.residual_likelihood
                              )}
                              : {riskData.residual_impact * riskData.residual_likelihood}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className='fw-semibold  d-block fs-7'>
                              {riskData?.added_controls
                                ? riskData?.added_controls.length + ' ' + 'controls'
                                : '--'}
                              {riskData?.added_controls ? (
                                <OverlayTrigger
                                  placement='right'
                                  popperConfig={{
                                    modifiers: [
                                      {
                                        name: 'offset',
                                        options: {
                                          offset: [0, 8],
                                        },
                                      },
                                    ],
                                  }}
                                  overlay={
                                    <Tooltip id='tooltip-top'>
                                      {/* <ol> */}
                                      {riskData.added_controls.map((control, index) => (
                                        // <li key={index} className='p-0'>
                                        <span style={{fontSize: '12px'}}>
                                          {control.control_title}
                                        </span>
                                        // </li>
                                      ))}
                                      {/* </ol> */}
                                    </Tooltip>
                                  }
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='16'
                                    height='16'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#5A5A5A'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-info-circle mx-2'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                                    <path d='M12 9h.01' />
                                    <path d='M11 12h1v4h1' />
                                  </svg>
                                </OverlayTrigger>
                              ) : (
                                ''
                              )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            {riskData?.owner ? (
                              <>
                                <OwnerDisplay owner={riskData.owner} uid={riskData.owner_uid} />
                              </>
                            ) : (
                              <span>--</span>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className='d-flex flex-column w-100 me-2'>
                            <div className='d-flex flex-stack mb-2'>
                              <div className='clearfix bshadow0 pbs fw-semibold'>
                                <span
                                  className={`badge fs-7 ${
                                    riskData?.risk_status === 'Incomplete'
                                      ? 'badge-danger'
                                      : riskData?.risk_status === 'Needs approval'
                                      ? 'badge-warning'
                                      : 'badge-primary'
                                  }`}
                                  style={{
                                    color:
                                      riskData?.risk_status === 'Incomplete'
                                        ? '#EE3043'
                                        : riskData?.risk_status === 'Needs approval'
                                        ? '#FFA800'
                                        : '#0E9F6E',
                                    background:
                                      riskData?.risk_status === 'Incomplete'
                                        ? '#FFF5F8'
                                        : riskData?.risk_status === 'Needs approval'
                                        ? '#FFF8DD'
                                        : '#E8F8F4',
                                  }}
                                >
                                  {riskData?.risk_status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className='dropdown mx-2'>
                            <button
                              className='btn btn-secondary border-0 p-0 m-2'
                              type='button'
                              id='dropdownMenu2'
                              data-bs-toggle='dropdown'
                              aria-haspopup='true'
                              aria-expanded='false'
                            >
                              <img
                                src='/media/buttons/Options.svg'
                                alt='Options'
                                onClick={toggleDropdown}
                              />
                            </button>
                            <ul
                              className='dropdown-menu'
                              aria-labelledby='dropdownMenu2'
                              style={{zIndex: 1}}
                            >
                              <li>
                                <button
                                  className='dropdown-item text-hover-primary'
                                  type='button'
                                  style={{color: '#262626'}}
                                  onClick={() => handleOpenModal(riskData.id)}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-edit mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                    <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                    <path d='M16 5l3 3' />
                                  </svg>{' '}
                                  {currentUser?.role === 'Auditor' || currentAuditContext
                                    ? 'View Risk Details'
                                    : 'Edit Risk'}
                                </button>
                              </li>

                              <li>
                                <button
                                  className={`dropdown-item ${
                                    currentUser?.role === 'Auditor' || currentAuditContext
                                      ? 'text-gray-600'
                                      : 'text-hover-danger'
                                  } `}
                                  type='button'
                                  style={{color: '#F15363'}}
                                  onClick={(e) => deleteHandler(riskData)}
                                  disabled={
                                    currentUser?.role === 'Auditor' || !!currentAuditContext
                                  }
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M4 7l16 0' />
                                    <path d='M10 11l0 6' />
                                    <path d='M14 11l0 6' />
                                    <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                    <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                  </svg>{' '}
                                  Delete Risk
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )
                  })
              ) : (
                <tr style={{height: '100%'}}>
                  {searchLength ? (
                    <td colSpan={8}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        <div className='m-20'>
                          <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                          <div className='m-5 fs-5'>
                            <p style={{fontWeight: 'bold'}}>No risk scenario found!</p>
                          </div>
                          <p>
                            We couldn't find any risk scenario that match your search. Maybe try a
                            different search!
                          </p>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td colSpan={8}>
                      <div
                        className='d-flex justify-content-center align-items-center text-center'
                        style={{height: '100%'}}
                      >
                        {!showEmptyState ? (
                          <div className='m-20'>
                            <RiskListLoading />
                          </div>
                        ) : (
                          <div className='m-20'>
                            <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                            <div className='m-4 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No Risks Scenario Added</p>
                            </div>
                            {/* <p>Click on 'Add Scenario' to assess risk</p> */}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => setIsDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedRisk?.riskSource}
        isLoading={isDeleting}
        title={`Remove ${selectedRisk?.riskSource}`}
        message='Are you sure you want to remove this risk? This will also remove its association from all linked controls. This action cannot be undone.'
        type='delete'
      />
    </>
  )
}

export {RiskManagement}
